import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import SalesAppHeader from "../components/SalesAppHeader";
import {
  searchOffers,
  saveOfferTracking,
  getOfferRead,
  createOffer,
  getOfferReadByKey,
} from "../api/salesClient";
import { decodeOfferId } from "../../lib/offer-key.mjs";
import { listUnitsByProject } from "../services/unitsClient";
import { resolveFloorplanUrl } from "../utils/floorplanUrl";
import { generateOfferPdf } from "../utils/offerPdf";

function formatPhone(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 11 && digits.startsWith("1")) {
    const local = digits.slice(1);
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 7) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return String(value || "").trim();
}

const DATE_FIELDS = [
  "contract_sent_date",
  "fully_executed_date",
  "week_ratified_date",
  "projected_closing_date",
  "initial_deposit_receipt_date",
  "financing_contingency_date",
  "loan_app_complete",
  "loan_approved",
  "loan_lock",
  "appraisal_ordered",
  "appraiser_visit_date",
  "appraisal_complete",
  "walk_through_date",
  "extended_adjusted_coe",
  "notice_to_close",
  "loan_docs_ordered",
  "loan_docs_signed",
  "loan_fund",
  "coe_date",
  "buyer_complete",
  "envelope_sent_date",
  "buyer_sign_date",
];

const DATE_FIELD_LABELS = {
  week_ratified_date: "ratified date",
  extended_adjusted_coe: "extended/adjusted coe",
};

const DATE_FIELDS_UI = DATE_FIELDS.filter((field) => field !== "fully_executed_date");

const NUMERIC_FIELDS = [
  "base_price",
  "final_price",
  "list_price",
  "initial_deposit_amount",
  "seller_credit",
  "upgrade_credit",
  "total_upgrades_solar",
  "hoa_credit",
  "total_credits",
];

const CREDIT_FIELDS = ["seller_credit", "upgrade_credit", "total_upgrades_solar", "hoa_credit"];
const NUMERIC_FIELDS_SET = new Set(NUMERIC_FIELDS);
const FIELD_ALIASES = {
  week_ratified_date: ["fully_executed_date"],
  fully_executed_date: ["week_ratified_date"],
  extended_adjusted_coe: ["adjusted_coe"],
};

const STATUS_NUMERIC_MAP = {
  "pending release": 5,
  pending: 5,
  available: 4,
  "contract sent": 3,
  contract_sent: 3,
  "ratified - fully executed": 2,
  ratified: 2,
  closed: 1,
};

const SALES_PROJECT_OPTIONS = ["Aria", "Fusion", "SoMi A", "SoMi B", "SoMi Towns", "Vida"];
const AUTO_FIT_WIDE = "repeat(auto-fit, minmax(220px, 1fr))";
const AUTO_FIT_MEDIUM = "repeat(auto-fit, minmax(180px, 1fr))";
const AUTO_FIT_NARROW = "repeat(auto-fit, minmax(140px, 1fr))";
const UI_STATUS_OPTIONS = [
  { value: "closed", label: "Closed" },
  { value: "ratified", label: "Ratified" },
  { value: "offer_received", label: "Offer Rec." },
  { value: "available", label: "Available" },
  { value: "pending_release", label: "Pend. Rel." },
  { value: "canceled", label: "Canceled" },
];
const DEFAULT_UI_STATUS = "available";
const RESETTABLE_FORM_FIELDS = [
  "buyers_combined",
  "buyer_name",
  "buyer_1__full_name",
  "buyer_2_full_name",
  "buyer1_email",
  "buyer_1_email",
  "buyer_2_email",
  "buyer_mobile_phone",
  "address_1",
  "address_2",
  "city",
  "state",
  "zip_code",
  "primary_lender",
  "primary_loan_officer_full_name",
  "primary_loan_officer",
  "agent_brokerage",
  "referring_agent_full_name",
  "referring_agent_email",
  "purchase_type",
  "notes",
  "docusign_envelope",
  "cash_purchase",
  "cash",
  "investor_owner",
  ...DATE_FIELDS,
  ...NUMERIC_FIELDS,
];

function statusNumericFor(value) {
  const key = String(value || "").trim().toLowerCase();
  if (!key) return "";
  if (key === "canceled" || key === "cancelled") return "";
  return STATUS_NUMERIC_MAP[key] ?? "";
}

function resolveUiStatus(statusValue, offerStatusValue) {
  const offerStatus = String(offerStatusValue || "").trim().toLowerCase();
  if (offerStatus === "offer_received") return "offer_received";

  const key = String(statusValue || "").trim().toLowerCase();
  if (key === "closed") return "closed";
  if (key === "ratified" || key === "ratified - fully executed" || key === "backlog") return "ratified";
  if (key === "pending" || key === "pending release" || key === "pending_release") return "pending_release";
  if (key === "canceled" || key === "cancelled") return "canceled";
  if (key === "available" || key === "inventory") return "available";
  return DEFAULT_UI_STATUS;
}

function labelForUiStatus(value) {
  return UI_STATUS_OPTIONS.find((option) => option.value === value)?.label || humanizeStatus(value);
}

function syncCanonicalStatus(target, uiStatusValue) {
  if (!target) return target;
  const uiStatus = String(uiStatusValue || target.ui_status || "").trim().toLowerCase() || DEFAULT_UI_STATUS;
  target.ui_status = uiStatus;

  if (uiStatus === "closed") {
    target.status = "closed";
    target.statusnumeric = 1;
    delete target.offer_status;
    delete target.offer_status_label;
    applyStatusDerivedDates(target, "closed");
    return target;
  }

  if (uiStatus === "ratified") {
    target.status = "ratified";
    target.statusnumeric = 2;
    delete target.offer_status;
    delete target.offer_status_label;
    applyStatusDerivedDates(target, "ratified");
    return target;
  }

  if (uiStatus === "offer_received") {
    target.status = "Available";
    target.statusnumeric = 4;
    target.offer_status = "offer_received";
    target.offer_status_label = "Offer Rec.";
    return target;
  }

  if (uiStatus === "pending_release") {
    target.status = "Pending";
    target.statusnumeric = 5;
    delete target.offer_status;
    delete target.offer_status_label;
    return target;
  }

  if (uiStatus === "canceled") {
    target.status = "Available";
    target.statusnumeric = 4;
    delete target.offer_status;
    delete target.offer_status_label;
    return target;
  }

  target.status = "Available";
  target.statusnumeric = 4;
  delete target.offer_status;
  delete target.offer_status_label;
  return target;
}

function resetOfferFormState(target) {
  if (!target) return target;
  RESETTABLE_FORM_FIELDS.forEach((field) => {
    if (field === "list_price") return;
    if (NUMERIC_FIELDS_SET.has(field)) {
      target[field] = "";
      return;
    }
    if (field === "cash_purchase") {
      target[field] = false;
      return;
    }
    delete target[field];
  });

  target.ui_status = "available";
  target.status = "Available";
  target.statusnumeric = 4;
  delete target.offer_status;
  delete target.offer_status_label;
  delete target.status_date;
  target.final_price = 0;
  target.cash = "0.0";
  return target;
}

function sanitizePayload(payload) {
  return Object.fromEntries(
    Object.entries(payload || {}).filter(([, value]) => value !== undefined)
  );
}

function formatDateForInput(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
}

function normalizeNumeric(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  return Number.isFinite(num) ? num : "";
}

function todayInputDate() {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 10)
}

function humanizeStatus(value) {
  const key = String(value || "").trim();
  if (!key) return "existing";
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function applyStatusDerivedDates(target, statusValue) {
  const key = String(statusValue || target?.status || "").trim().toLowerCase();
  if (!key || !target) return target;

  if (key === "closed" && !target.coe_date) {
    target.coe_date = todayInputDate();
  }

  if (key === "ratified" || key === "ratified - fully executed" || key === "backlog") {
    const ratifiedDate =
      target.week_ratified_date || target.fully_executed_date || todayInputDate();
    if (!target.week_ratified_date) {
      target.week_ratified_date = ratifiedDate;
    }
    if (!target.fully_executed_date) {
      target.fully_executed_date = ratifiedDate;
    }
  }

  return target;
}

function validateStatusAndClosingDate(payload) {
  const status = String(payload?.status || "").trim().toLowerCase();
  const coeDate = String(payload?.coe_date || "").trim();

  if (coeDate && status !== "closed") {
    return "COE date requires the status to be set to Closed before saving.";
  }

  return "";
}

function calculateTotalCredits(data) {
  const anyFilled = CREDIT_FIELDS.some((key) => {
    const val = data[key];
    return val !== undefined && val !== "" && Number.isFinite(Number(val));
  });
  if (!anyFilled) return "";
  return CREDIT_FIELDS.reduce((acc, key) => acc + (Number(data[key]) || 0), 0);
}

function normalizeOfferData(offer = {}, fallback = {}) {
  const src = offer || {};
  const fb = fallback || {};
  const next = {};

  const resolveValue = (key) => {
    const primary = src[key];
    if (primary !== undefined && primary !== null && primary !== "") return primary;
    const secondary = fb[key];
    if (secondary !== undefined && secondary !== null && secondary !== "") return secondary;
    return "";
  };

  const resolveWithAliases = (key) => {
    const value = resolveValue(key);
    if (value !== "") return value;
    const aliases = FIELD_ALIASES[key];
    if (!aliases) return "";
    for (const alias of aliases) {
      const aliasValue = resolveValue(alias);
      if (aliasValue !== "") return aliasValue;
    }
    return "";
  };

  const resolvedOfferId = resolveValue("offerId") || fb.offerId || "";
  next.offerId = resolvedOfferId;
  if (resolvedOfferId) {
    const decoded = decodeOfferId(resolvedOfferId);
    if (decoded.projectId && !src.project_id && !fb.project_id) {
      next.project_id = decoded.projectId;
    }
    if (decoded.contractUnitNumber && !src.contract_unit_number && !fb.contract_unit_number) {
      next.contract_unit_number = decoded.contractUnitNumber;
    }
  }

  next.project_id = next.project_id || resolveValue("project_id") || "Fusion";
  next.contract_unit_number =
    next.contract_unit_number || resolveValue("contract_unit_number") || resolveValue("unit_number") || "";
  next.unit_number = resolveValue("unit_number") || next.contract_unit_number || "";
  next.unit_name = resolveValue("unit_name") || fb.unit_name || "";

  let buyerCombined = resolveValue("buyers_combined") || resolveValue("buyer_name");
  if (!buyerCombined && (resolveValue("buyer_1__full_name") || resolveValue("buyer_2_full_name"))) {
    const b1 = resolveValue("buyer_1__full_name") || "";
    const b2 = resolveValue("buyer_2_full_name") || "";
    buyerCombined = [b1, b2].filter(Boolean).join(" and ");
  }
  next.buyer_name = buyerCombined;
  next.buyers_combined = buyerCombined;

  const textFields = [
    "buyer_1__full_name",
    "buyer_2_full_name",
    "buyer1_email",
    "buyer_1_email",
    "buyer_2_email",
    "buyer_mobile_phone",
    "address_1",
    "address_2",
    "city",
    "state",
    "zip_code",
    "primary_lender",
    "primary_loan_officer_full_name",
    "primary_loan_officer",
    "agent_brokerage",
    "referring_agent_full_name",
    "referring_agent_email",
    "purchase_type",
  ];

  textFields.forEach((field) => {
    const val = resolveValue(field);
    if (val !== "") next[field] = val;
  });

  if (next.buyer_1_email && !next.buyer1_email) next.buyer1_email = next.buyer_1_email;
  if (next.primary_loan_officer && !next.primary_loan_officer_full_name) {
    next.primary_loan_officer_full_name = next.primary_loan_officer;
  }

  const cashVal = resolveValue("cash");
  if (cashVal === "true" || cashVal === true) next.cash_purchase = true;

  const invVal = resolveValue("investor_owner");
  if (invVal === "true" || invVal === true) next.purchase_type = "investment_property";

  const statusVal = resolveValue("status");
  if (statusVal !== "") next.status = statusVal;
  const offerStatusVal = resolveValue("offer_status");
  if (offerStatusVal !== "") next.offer_status = offerStatusVal;
  const offerStatusLabelVal = resolveValue("offer_status_label");
  if (offerStatusLabelVal !== "") next.offer_status_label = offerStatusLabelVal;
  next.ui_status = resolveUiStatus(statusVal, offerStatusVal);
  syncCanonicalStatus(next, next.ui_status);
  const envelopeVal = resolveValue("docusign_envelope");
  if (envelopeVal !== "") next.docusign_envelope = envelopeVal;
  const notesVal = resolveValue("notes");
  if (notesVal !== "") next.notes = notesVal;

  DATE_FIELDS.forEach((field) => {
    const formatted = formatDateForInput(resolveWithAliases(field));
    if (formatted) {
      next[field] = formatted;
    }
  });

  NUMERIC_FIELDS.forEach((field) => {
    if (field === "total_credits") return;
    const numeric = normalizeNumeric(resolveValue(field));
    if (numeric !== "") {
      next[field] = numeric;
    }
  });

  const credits = calculateTotalCredits({ ...fb, ...src, ...next });
  if (credits !== "") {
    next.total_credits = credits;
  }

  return next;
}

export default function UnifiedForm() {
  const auth = useAuth();
  const jwt = auth?.user?.idToken || auth?.user?.accessToken || null;

  const [form, setForm] = useState({ project_id: "Fusion", ui_status: DEFAULT_UI_STATUS, status: "Available", statusnumeric: 4 });
  const [projectUnits, setProjectUnits] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const [buildingInfo, setBuildingInfo] = useState("");
  const [planInfo, setPlanInfo] = useState("");
  const [addressInfo, setAddressInfo] = useState("");
  const pdfBtnRef = useRef(null);

  const projectOptions = SALES_PROJECT_OPTIONS;

  const unitOptions = useMemo(() => {
    return [...projectUnits]
      .filter((unit) => {
        const unitNumber = String(unit?.unit_number ?? "").trim().replace(/\.0$/, "");
        const salesStatusNum = Number(unit?.sales_status?.statusnumeric);
        const salesStatusText = String(
          unit?.sales_status?.status ||
          unit?.sales_status?.status_key ||
          unit?.sales_status?.status_label ||
          ""
        ).trim().toLowerCase();
        const isClosed = salesStatusNum === 1 || salesStatusText === "closed";
        return unitNumber && !isClosed;
      })
      .filter((unit) => unit?.unit_number !== undefined && unit?.unit_number !== null && unit?.unit_number !== "")
      .sort((a, b) =>
        String(a.unit_number).localeCompare(String(b.unit_number), undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );
  }, [projectUnits]);

  const fetchUnitsForProject = async (projectIdToLook) => {
    if (!projectIdToLook) return [];
    const data = await listUnitsByProject({ project_id: projectIdToLook });
    return Array.isArray(data?.items) ? data.items : [];
  };

  useEffect(() => {
    let cancelled = false;
    async function loadProjectUnits() {
      if (!form.project_id) {
        setProjectUnits([]);
        return;
      }
      try {
        const items = await fetchUnitsForProject(form.project_id);
        if (!cancelled) {
          setProjectUnits(items);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn(`Failed to load units for project ${form.project_id}`, err);
          setProjectUnits([]);
        }
      }
    }
    loadProjectUnits();
    return () => {
      cancelled = true;
    };
  }, [form.project_id]);

  useEffect(() => {
    if (!form.project_id || !form.unit_number) return;
    const selectedUnit = unitOptions.find(
      (unit) => String(unit.unit_number).replace(/\.0$/, "") === String(form.unit_number).replace(/\.0$/, "")
    );
    if (selectedUnit) {
      applyHomeDetails(selectedUnit);
    }
  }, [form.project_id, form.unit_number, unitOptions]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setIsLoading(true);
    setError("");
    setMsg("");
    try {
      if (!jwt) throw new Error("No JWT token available");
      const results = await searchOffers(jwt, q);
      setSearchResults(results);
    } catch (err) {
      console.error("Search error:", err);
      setError(err.message || "Search failed");
    } finally {
      setIsLoading(false);
    }
  };

  const selectResult = (item) => {
    hydrateSelection(item);
  };

  const hydrateSelection = async (item) => {
    setSearchResults([]);
    setError("");
    setMsg("");
    const base = normalizeOfferData({}, item);
    setForm(base);

    try {
      if (!jwt) throw new Error("No JWT token available");
      const record = await getOfferRead(jwt, item.offerId);
      const hydrated = normalizeOfferData(record, item);

      
      setForm(hydrated);

      if (hydrated.project_id && hydrated.contract_unit_number) {
        lookupUnitDetails(hydrated.project_id, hydrated.contract_unit_number);
      }
    } catch (err) {
      console.error("Failed to load offer record:", err);
      setError(err.message || "Failed to load offer details");
      setForm(normalizeOfferData({}, item));
    }
  };

  const lookupUnitDetails = async (projectIdToLook, unitToLook) => {
    const n = String(unitToLook || "").trim();
    if (!n) return;
    try {
      const items = await fetchUnitsForProject(projectIdToLook);
      const checkN = String(n).trim().replace(/\.0$/, "");
      const found = items.find((u) => String(u.unit_number).replace(/\.0$/, "") === checkN);
      if (found) {
        applyHomeDetails(found);
      }
    } catch (e) {
      console.warn("Could not lookup unit detail silently");
    }
  };

  async function handleSelectUnit() {
    setMsg("");
    const n = String(form.unit_number || "").trim();
    if (!n) return setMsg("Enter a unit number.");

    const proj = form.project_id || "Fusion";

    try {
      const items = await fetchUnitsForProject(proj);
      const checkN = String(n).trim().replace(/\.0$/, "");
      const found = items.find((u) => String(u.unit_number).replace(/\.0$/, "") === checkN);

      if (found) {
        applyHomeDetails(found);
        if (jwt) {
          const formattedUnit = /^\d+$/.test(checkN) ? `${checkN}.0` : checkN;
          const record = await getOfferReadByKey(jwt, proj, formattedUnit);
          if (record) {
            setForm((prev) => normalizeOfferData(record, { ...prev, ...found }));
            setMsg(`Offer details loaded for ${proj} unit ${checkN}.`);
          } else {
            setMsg(`Home details loaded for ${proj} unit ${checkN}. No saved offer record found.`);
          }
        }
      } else {
        setMsg(`Unit ${n} not found in project ${proj}.`);
      }
    } catch (e) {
      setMsg(`Error fetching unit: ${String(e.message || e)}`);
    }
  }

  function applyHomeDetails(obj = {}) {
    const b =
      obj.building_info ||
      (obj.building_id ? `Building ${obj.building_id}, Unit ${obj.unit_number ?? ""}` : "") ||
      "";
    const p = obj.plan_info || obj.plan_type || "";
    const a = obj.address || "";

    setBuildingInfo(b);
    setPlanInfo(p);
    setAddressInfo(a);

    const pdf = resolveFloorplanUrl(obj.pdf_url || obj.floorplan_url || "");
    if (pdfBtnRef.current) {
      if (pdf) {
        pdfBtnRef.current.disabled = false;
        pdfBtnRef.current.onclick = () => window.open(pdf, "_blank", "noopener,noreferrer");
      } else {
        pdfBtnRef.current.disabled = true;
        pdfBtnRef.current.onclick = null;
      }
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let warningMessage = "";
    setForm((prev) => {
      const next = { ...prev };
      if (type === "checkbox") {
        next[name] = checked;
      } else if (NUMERIC_FIELDS_SET.has(name)) {
        const num = value === "" ? "" : Number(value);
        next[name] = Number.isFinite(num) ? num : "";
      } else {
        next[name] = value;
      }

      if (name === "ui_status") {
        syncCanonicalStatus(next, value);
        if (String(value || "").trim().toLowerCase() === "closed" && !String(next.coe_date || "").trim()) {
          warningMessage = "Is this unit Closed? Did you add the date to 'COE date' below?";
        }
      }
      if (name === "project_id") {
        next.unit_number = "";
        next.contract_unit_number = "";
        next.offerId = "";
        setBuildingInfo("");
        setPlanInfo("");
        setAddressInfo("");
      }
      if (name === "unit_number") {
        next.contract_unit_number = value;
        if (value !== prev.unit_number) {
          next.offerId = "";
        }
      }
      if (name === "week_ratified_date") {
        next.fully_executed_date = value;
      } else if (name === "fully_executed_date") {
        next.week_ratified_date = value;
      } else if (name === "coe_date") {
        if (String(value || "").trim() && String(next.status || "").trim().toLowerCase() !== "closed") {
          warningMessage = "Did you change the status to 'Closed' in the menu at the top?";
        }
      }
      next.total_credits = calculateTotalCredits(next);
      return next;
    });
    if (warningMessage) {
      window.alert(warningMessage);
    }
  };

  const handlePhoneBlur = (event) => {
    const formatted = formatPhone(event.target.value);
    setForm((prev) => ({ ...prev, [event.target.name]: formatted }));
  };

  function buildBuyersCombined(buyer1, buyer2) {
    const names = [buyer1, buyer2].filter(Boolean);
    if (!names.length) return "";
    if (names.length === 1) return names[0];
    return `${names[0]} and ${names[1]}`;
  }

  const saveTracking = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setMsg("");

    try {
      if (!jwt) throw new Error("No JWT token available");
      const unitRaw = String(form.contract_unit_number || form.unit_number || "").trim();
      const proj = form.project_id || "Fusion";

      if (!unitRaw) {
        throw new Error("Unit number required before saving.");
      }

      let formattedUnit = unitRaw;
      if (/^\d+$/.test(formattedUnit)) {
        formattedUnit = `${formattedUnit}.0`;
      }

      const buyer1 = String(form.buyer_1__full_name || "").trim();
      const buyer2 = String(form.buyer_2_full_name || "").trim();
      const buyersCombined =
        buildBuyersCombined(buyer1, buyer2) || form.buyers_combined || form.buyer_name;

      const payload = {
        ...form,
        contract_unit_number: formattedUnit,
        unit_number: formattedUnit,
        project_id: proj,
        buyers_combined: buyersCombined,
        buyer_current_address: form.address_1 || "",
        buyer_current_city: form.city || "",
        buyer_current_state: form.state || "",
        buyer_current_zip: form.zip_code || "",
        cash: form.cash_purchase ? "true" : "",
        investor_owner: form.purchase_type === "investment_property" ? "true" : "",
      };

      if ((!payload.project_id || !payload.contract_unit_number) && payload.offerId) {
        const decoded = decodeOfferId(payload.offerId);
        if (decoded.projectId && !payload.project_id) payload.project_id = decoded.projectId;
        if (decoded.contractUnitNumber && !payload.contract_unit_number) {
          payload.contract_unit_number = decoded.contractUnitNumber;
        }
      }
      syncCanonicalStatus(payload, payload.ui_status);
      if (payload.ui_status === "canceled") {
        const cancelConfirmed = window.confirm(
          "Are you sure? Saving Canceled will clear the offer details and reset this unit back to Available."
        );
        if (!cancelConfirmed) {
          setMsg("Save canceled.");
          return;
        }
        resetOfferFormState(payload);
      }

      if (payload.status) {
        if (payload.status === "closed" && payload.coe_date) {
          payload.status_date = payload.coe_date;
        } else if (
          String(payload.status).trim().toLowerCase() === "ratified" &&
          (payload.week_ratified_date || payload.fully_executed_date)
        ) {
          payload.status_date = payload.week_ratified_date || payload.fully_executed_date;
        } else if (!payload.status_date) {
          payload.status_date = new Date().toISOString();
        }
      }
      const validationError = validateStatusAndClosingDate(payload);
      if (validationError) {
        throw new Error(validationError);
      }
      if (payload.week_ratified_date && !payload.fully_executed_date) {
        payload.fully_executed_date = payload.week_ratified_date;
      } else if (payload.fully_executed_date && !payload.week_ratified_date) {
        payload.week_ratified_date = payload.fully_executed_date;
      }

      let resMsg = "";
      const shouldGeneratePdf = !payload.offerId;
      let savedOfferId = payload.offerId || "";

      if (payload.offerId) {
        await saveOfferTracking(jwt, sanitizePayload(payload));
        setForm(normalizeOfferData(payload));
        resMsg =
          String(form.ui_status || "").trim().toLowerCase() === "canceled"
            ? "Record reset to Available."
            : "Tracking saved.";
      } else {
        const existing = await getOfferReadByKey(jwt, proj, formattedUnit);

        if (existing) {
          const existingStatus = String(existing.status || "").trim().toLowerCase();
          if (existingStatus === "closed") {
            throw new Error(
              `A Closed record already exists for ${proj} unit ${formattedUnit}. Check that you selected the correct project and unit.`
            );
          }

          const existingBuyer = String(
            existing.buyers_combined ||
              existing.buyer_name ||
              existing.buyer_1__full_name ||
              ""
          ).trim();
          const confirmMessage = [
            `A ${humanizeStatus(existing.status)} record already exists for ${proj} unit ${formattedUnit}.`,
            existingBuyer ? `Existing buyer: ${existingBuyer}.` : "",
            "Saving now will overwrite that record.",
            "Do you want to continue?",
          ]
            .filter(Boolean)
            .join(" ");

          if (!window.confirm(confirmMessage)) {
            setMsg("Save canceled.");
            return;
          }

          const overwritePayload = {
            ...existing,
            ...payload,
            offerId: existing.offerId || `${proj}#${formattedUnit}`,
            created_at: existing.created_at || payload.created_at,
          };
          if (String(form.ui_status || "").trim().toLowerCase() === "canceled") {
            resetOfferFormState(overwritePayload);
          }
          await saveOfferTracking(jwt, sanitizePayload(overwritePayload));
          savedOfferId = overwritePayload.offerId;
          setForm(normalizeOfferData(overwritePayload));
          resMsg =
            String(form.ui_status || "").trim().toLowerCase() === "canceled"
              ? "Record reset to Available."
              : "Existing offer overwritten after confirmation.";
        } else {
          const saveRes = await createOffer(jwt, sanitizePayload(payload));
          const newOfferId = saveRes?.offerId || saveRes?.offer_id;
          if (newOfferId) {
            savedOfferId = newOfferId;
            setForm((prev) => normalizeOfferData({ ...payload, offerId: newOfferId }, prev));
          }
          resMsg =
            String(form.ui_status || "").trim().toLowerCase() === "canceled"
              ? "Record reset to Available."
              : "Offer created and tracking saved.";
        }
      }

      if (shouldGeneratePdf) {
        try {
          const pdfBlob = await generateOfferPdf({
            ...payload,
            offerId: savedOfferId || `${proj}#${formattedUnit}`,
            buyer_name: buyersCombined,
            building_info: buildingInfo,
            plan_info: planInfo,
            address: addressInfo,
          });
          const pdfUrl = window.URL.createObjectURL(pdfBlob);
          const link = document.createElement("a");
          link.href = pdfUrl;
          link.download = `offer-${proj.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${formattedUnit.replace(/[^a-z0-9.]+/gi, "-")}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(pdfUrl);
          resMsg += " PDF generated.";
        } catch (pdfError) {
          resMsg += ` PDF generation failed: ${pdfError.message || pdfError}`;
        }
      }

      setMsg(resMsg);
    } catch (e) {
      setMsg(`Save error: ${e.message || "Save failed"}`);
    }
  };

  const formatCurrency = (val) => {
    if (val === undefined || val === "") return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(val);
  };

  return (
    <div id="unified-form" style={styles.container}>
      <SalesAppHeader title="Unified Offer & Tracking Form" projectId={form.project_id} />

      <form
        onSubmit={handleSearch}
        style={{
          marginBottom: "1rem",
          display: "flex",
          gap: "0.5rem",
          flexWrap: "wrap",
          padding: "16px",
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 8,
        }}
      >
        <input
          type="text"
          placeholder="Lookup by buyer or unit, click on the results below to hydrate the form."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ flex: "1 1 240px" }}
        />
        <button type="submit">Lookup</button>
        <button
          type="button"
          onClick={() => {
            setForm({ project_id: "Fusion", ui_status: DEFAULT_UI_STATUS, status: "Available", statusnumeric: 4 });
            setSearchResults([]);
            setMsg("");
          }}
        >
          Clear New
        </button>
        <button type="button" onClick={saveTracking}>
          {form.offerId ? "Save Tracking Update" : "Save New Offer + PDF"}
        </button>
      </form>

      {isLoading && <p>Searching…</p>}
      {error && <p style={{ color: "red", padding: "0 16px" }}>{error}</p>}

      {searchResults.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            padding: "0 16px",
            marginBottom: "1rem",
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 8,
          }}
        >
          {searchResults.map((item) => (
            <li
              key={item.offerId}
              style={{ cursor: "pointer", padding: "0.75rem", borderBottom: "1px solid #eee" }}
              onClick={() => selectResult(item)}
            >
              <strong>{item.project_id || "?"}</strong> /{" "}
              {item.contract_unit_number || item.unit_number || "?"} -{" "}
              {item.buyer_name || "Unknown"} - {labelForUiStatus(resolveUiStatus(item.status, item.offer_status))}
            </li>
          ))}
        </ul>
      )}

      {msg && (
        <div
          style={{
            marginBottom: 10,
            padding: 12,
            background:
              msg.toLowerCase().includes("error") || msg.toLowerCase().includes("failed")
                ? "#ffebee"
                : "#e8f5e9",
            color:
              msg.toLowerCase().includes("error") || msg.toLowerCase().includes("failed")
                ? "crimson"
                : "green",
            borderRadius: 6,
          }}
        >
          {msg}
        </div>
      )}

      <form onSubmit={saveTracking} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <section style={styles.section}>
          <h3 style={styles.legend}>Offer Basics & Buyer Input</h3>

          <div style={{ ...styles.row, gridTemplateColumns: "1fr", marginBottom: 12 }}>
            <div style={styles.inlineActionRow}>
              <button type="button" onClick={handleSelectUnit} style={styles.actionButton}>
                Show Offer Details
              </button>
            </div>
          </div>

          <div style={{ ...styles.row, gridTemplateColumns: AUTO_FIT_NARROW }}>
            <label style={styles.col}>
              Project
              <select
                name="project_id"
                value={form.project_id || "Fusion"}
                onChange={handleChange}
                style={styles.input}
              >
                {projectOptions.map((projectId) => (
                  <option key={projectId} value={projectId}>
                    {projectId}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.col}>
              Unit # *
              <select
                name="unit_number"
                value={form.unit_number || ""}
                onChange={handleChange}
                style={styles.input}
              >
                <option value="">Select Unit</option>
                {unitOptions.map((unit) => {
                  const value = String(unit.unit_number);
                  return (
                    <option key={`${unit.project_id}-${value}`} value={value}>
                      {value}
                    </option>
                  );
                })}
              </select>
            </label>

            <label style={styles.col}>
              Status
              <select
                name="ui_status"
                value={form.ui_status || DEFAULT_UI_STATUS}
                onChange={handleChange}
                style={styles.input}
              >
                {UI_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div style={{ ...styles.row, gridTemplateColumns: AUTO_FIT_WIDE, marginTop: 12 }}>
            <label style={styles.col}>
              Buyer (Combined)
              <input
                name="buyers_combined"
                value={form.buyers_combined || ""}
                onChange={handleChange}
                maxLength={100}
                style={styles.input}
              />
            </label>

            <label style={styles.col}>
              Buyer 1 Name
              <input
                name="buyer_1__full_name"
                value={form.buyer_1__full_name || ""}
                onChange={handleChange}
                maxLength={50}
                style={styles.input}
              />
            </label>

            <label style={styles.col}>
              Buyer 2 Name
              <input
                name="buyer_2_full_name"
                value={form.buyer_2_full_name || ""}
                onChange={handleChange}
                maxLength={50}
                style={styles.input}
              />
            </label>
          </div>

          <div style={{ ...styles.row, gridTemplateColumns: "1fr", marginTop: 12 }}>
            <label style={styles.col}>
              Current Street Address
              <input name="address_1" value={form.address_1 || ""} onChange={handleChange} style={styles.input} />
            </label>
          </div>

          <div style={{ ...styles.row, gridTemplateColumns: AUTO_FIT_NARROW, marginTop: 12 }}>
            <label style={styles.col}>
              City
              <input name="city" value={form.city || ""} onChange={handleChange} maxLength={55} style={styles.input} />
            </label>
            <label style={styles.col}>
              State
              <input name="state" value={form.state || ""} onChange={handleChange} maxLength={2} style={styles.input} />
            </label>
            <label style={styles.col}>
              Zip Code
              <input name="zip_code" value={form.zip_code || ""} onChange={handleChange} maxLength={15} style={styles.input} />
            </label>
          </div>

          <div style={{ ...styles.row, gridTemplateColumns: AUTO_FIT_MEDIUM, marginTop: 12 }}>
            <label style={styles.col}>
              Mobile Phone
              <input
                name="buyer_mobile_phone"
                value={form.buyer_mobile_phone || ""}
                onChange={handleChange}
                onBlur={handlePhoneBlur}
                maxLength={20}
                style={styles.input}
              />
            </label>
            <label style={styles.col}>
              Buyer 1 Email
              <input
                type="email"
                name="buyer1_email"
                value={form.buyer1_email || ""}
                onChange={handleChange}
                maxLength={40}
                style={styles.input}
              />
            </label>
            <label style={styles.col}>
              Buyer 2 Email
              <input
                type="email"
                name="buyer_2_email"
                value={form.buyer_2_email || ""}
                onChange={handleChange}
                maxLength={40}
                style={styles.input}
              />
            </label>
          </div>
        </section>

        <section style={styles.section}>
          <h3 style={styles.legend}>Home Architecture Details</h3>
          <div style={{ ...styles.row, gridTemplateColumns: AUTO_FIT_MEDIUM }}>
            <label style={styles.col}>Building Info<input value={buildingInfo} readOnly style={styles.inputRO} /></label>
            <label style={styles.col}>Plan Info<input value={planInfo} readOnly style={styles.inputRO} /></label>
            <label style={styles.col}>Address<input value={addressInfo} readOnly style={styles.inputRO} /></label>
            <div style={styles.inlineActionRow}>
              <button type="button" ref={pdfBtnRef} disabled>
                Open Floorplan
              </button>
            </div>
          </div>
        </section>

        <section style={styles.section}>
          <h3 style={styles.legend}>Agency & Finance Sources</h3>
          <div style={{ ...styles.row, gridTemplateColumns: AUTO_FIT_MEDIUM }}>
            <label style={styles.col}>
              Lender Institution
              <input name="primary_lender" value={form.primary_lender || ""} onChange={handleChange} style={styles.input} />
            </label>
            <label style={styles.col}>
              Loan Officer
              <input
                name="primary_loan_officer_full_name"
                value={form.primary_loan_officer_full_name || ""}
                onChange={handleChange}
                style={styles.input}
              />
            </label>
            <label style={styles.col}>
              Referring Agent Name
              <input
                name="referring_agent_full_name"
                value={form.referring_agent_full_name || ""}
                onChange={handleChange}
                style={styles.input}
              />
            </label>
            <label style={styles.col}>
              Agent Brokerage
              <input
                name="agent_brokerage"
                value={form.agent_brokerage || ""}
                onChange={handleChange}
                style={styles.input}
              />
            </label>
          </div>

          <div style={{ ...styles.row, gridTemplateColumns: AUTO_FIT_MEDIUM, marginTop: 12 }}>
            <label style={{ ...styles.col, alignItems: "center", flexDirection: "row", gap: 8 }}>
              <input type="checkbox" name="cash_purchase" checked={form.cash_purchase || false} onChange={handleChange} />
              Cash Purchase?
            </label>
            <label style={styles.col}>
              Purchase Type
              <select name="purchase_type" value={form.purchase_type || ""} onChange={handleChange} style={styles.input}>
                <option value="">Select...</option>
                <option value="personal_residence">Personal Residence</option>
                <option value="investment_property">Investment Property</option>
              </select>
            </label>
          </div>
        </section>
        <section style={styles.section}>
          <h3 style={styles.legend}>Key Dates</h3>
          <div style={{ ...styles.row, gridTemplateColumns: AUTO_FIT_MEDIUM }}>
            {DATE_FIELDS_UI.map((field) => (
              <label key={field} style={styles.col}>
                {DATE_FIELD_LABELS[field] || field.replace(/_/g, " ")}
                <input
                  type="date"
                  name={field}
                  value={form[field] || ""}
                  onChange={handleChange}
                  style={styles.input}
                />
              </label>
            ))}
          </div>
        </section>

        <section style={styles.section}>
          <h3 style={styles.legend}>Financials</h3>
          <div style={{ ...styles.row, gridTemplateColumns: AUTO_FIT_MEDIUM }}>
            {[
              "final_price",
              "list_price",
              "initial_deposit_amount",
              "seller_credit",
              "upgrade_credit",
              "total_upgrades_solar",
              "hoa_credit",
              "total_credits",
            ].map((field) => (
              <label key={field} style={styles.col}>
                {field === "final_price" ? "offer/final price ($)" : `${field.replace(/_/g, " ")} ($)`}
                <input
                  type="number"
                  name={field}
                  value={form[field] ?? ""}
                  onChange={handleChange}
                  step="1"
                  min="0"
                  inputMode="numeric"
                  style={styles.input}
                />
                {form[field] && <small style={{ color: "#555" }}>{formatCurrency(form[field])}</small>}
              </label>
            ))}
          </div>
        </section>

        <section style={styles.section}>
          <h3 style={styles.legend}>DocuSign & Tracking Notes</h3>
          <div style={{ ...styles.row, gridTemplateColumns: AUTO_FIT_MEDIUM }}>
            <label style={{ ...styles.col, gridColumn: "1 / -1" }}>
              DocuSign Envelope
              <input
                type="text"
                name="docusign_envelope"
                value={form.docusign_envelope || ""}
                onChange={handleChange}
                style={styles.input}
              />
            </label>
            <label style={{ ...styles.col, gridColumn: "1 / -1" }}>
              Notes
              <textarea
                name="notes"
                value={form.notes || ""}
                onChange={handleChange}
                rows={4}
                style={styles.textarea}
              />
            </label>
          </div>
        </section>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="submit">{form.offerId ? "Save Tracking Update" : "Save New Offer + PDF"}</button>
        </div>
      </form>
    </div>
  );
}

const styles = {
  container: { maxWidth: 1100, margin: "0 auto", padding: 16, fontFamily: "Lato, Arial, sans-serif" },
  section: { background: "#fff", border: "1px solid #ddd", borderRadius: 8, padding: 16 },
  legend: { margin: 0, marginBottom: 12, fontWeight: 700, fontSize: "1.05rem", color: "#333" },
  row: { display: "grid", gap: 12, alignItems: "end" },
  inlineActionRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, minWidth: 0 },
  actionButton: { maxWidth: "100%" },
  col: { display: "flex", flexDirection: "column", minWidth: 0 },
  input: { padding: "10px", border: "1px solid #ccc", borderRadius: 6, fontSize: "1rem" },
  inputRO: { padding: "10px", border: "1px solid #eee", borderRadius: 6, fontSize: "1rem", background: "#f5f5f5", color: "#555" },
  textarea: { padding: "10px", border: "1px solid #ccc", borderRadius: 6, fontSize: "1rem", resize: "vertical" },
};




